import React, { useState, useEffect, useContext } from 'react'
import ProductsListHeader from './ProductsListHeader'
import ProductsListInner from './ProductsListInner'
import ProductsListFooter from './ProductsListFooter'
import { appContext } from '../../../App'
import { getProductsList, getProductsListSettings, saveSettings, deleteProduct,
  getWarehouses } from '../ProductsFunctions'

export const productsListControllerContext = React.createContext()

function useProductsListController() {
  const app = useContext(appContext)
  const [productsListSettings, setProductsListSettings] = useState(null)
  const [warehouses, setWarehouses] = useState(null)
  const [warehouse, setWarehouse] = useState(null)
  const [products, setProducts] = useState([])
  const [productsCopy, setProductsCopy] = useState([])
  const [itemsToDelete, setItemsToDelete] = useState([])
  const [recordFound, setRecordFound] = useState(true)
  const [recordPerPage, setRecordPerPage] = useState(null)
  const [recPP, setRecPP] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPage, setTotalPage] = useState(0)
  const [selectAll, setSelectAll] = useState(false)
  const [startSlice, setStartSlice] = useState(0)
  const [endSlice, setEndSlice] = useState(0)

  const addItemToDeleteList = (product_id) => setItemsToDelete([...itemsToDelete, product_id])
  const removeItemsToDeleteList = (product_id) => setItemsToDelete([...itemsToDelete.filter(pid => pid !== product_id)])
  const clearItemToDeleteList = () => setItemsToDelete([])

  const filterProducts = async (searchString) => {
    if (!searchString) reloadProductsList()

    let tempProducts = []

    // Create products item_full_description element on productsCopy object on-the-fly
    productsCopy.forEach((product, i) => {
        product.item_full_description = `${product.brand} ${product.name} ${product.description} ${product.classification}`
    })

    productsCopy.forEach(products_item => {
      let found = true
      searchString.split(' ').forEach((search_string_item, i) => {
        found = (found && (products_item.item_full_description.toLowerCase().search(search_string_item.toLowerCase()) !== -1))

        // if search string has barcode on it also find it too
        products_item.units.forEach((item_unit, i) => {
          found = (found || (item_unit.id === search_string_item))
        })
      })
      if (found) tempProducts.push(products_item)
    })

    // Remove checks
    setAllCheck(false)
    setSelectAll(false)

    setRecordFound(tempProducts.length > 0)
    if (tempProducts.length > 0) setProducts(tempProducts)
    setPage(1)
  }

  const deleteSelectedProducts = async () => {
    itemsToDelete.forEach((product_id) => deleteProduct(app, product_id))
    setItemsToDelete([])
  }

  const getTotalRecords = () => products.length

  const setAllCheck = (check) => {
    const itemCheckbox = document.querySelectorAll('.item-checkbox')
    itemCheckbox.forEach((item, i) => {
      item.checked = check
    })

    if (check)
      setItemsToDelete([...products.slice(startSlice, endSlice).map((product) => product._id)])
    else
      clearItemToDeleteList()
  }

  const updateSettings = async (settings) => {
    await saveSettings(app, settings)
    const products_list_settings = await getProductsListSettings(app)
    setProductsListSettings(products_list_settings)
    setRecordPerPage(products_list_settings.record_per_page)
    setRecPP(products_list_settings.record_per_page) // ?

    const warehouses = await getWarehouses(app)
    setWarehouses(warehouses)
    const settings_warehouse = warehouses.find(wh => wh._id === products_list_settings.warehouse_id)
    setWarehouse({name: settings_warehouse.name, id: settings_warehouse._id})
  }

  const reloadProductsList = async () => {
    const products_list = await getProductsList(app, warehouse.id)
    setRecordFound(products_list.length > 0)
    setProducts(products_list)
    setProductsCopy(products_list)
  }

  useEffect(() => {
    let isMounted = true

    const init = async () => {
      const warehouses = await getWarehouses(app)
      const products_list_settings = await getProductsListSettings(app)

      if (isMounted) {
        setWarehouses(warehouses)
        setProductsListSettings(products_list_settings)
        // to check if still necessary
        setRecordPerPage(products_list_settings.record_per_page)
        setRecPP(products_list_settings.record_per_page) // ?

        const settings_warehouse = warehouses.find(wh => wh._id === products_list_settings.warehouse_id)

        if (settings_warehouse) setWarehouse({name: settings_warehouse.name, id: settings_warehouse._id})
      }
    }

    if (isMounted) init()

    return(() => { isMounted = false })
    /* Hack to avoid the eslint warning */
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (warehouse && productsListSettings) { reloadProductsList() }
    /* Hack to avoid the eslint warning */
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouse, productsListSettings])

  useEffect(() => {
    if (recordPerPage) {
      const tot_page = Math.ceil(getTotalRecords() / recordPerPage)
      setTotalPage(tot_page)
    }
    /* Hack to avoid the eslint warning */
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, recordPerPage])

  return ({
    warehouse: [warehouse, setWarehouse],
    selectAll: [selectAll, setSelectAll],
    page: [page, setPage],
    recordPerPage: [recordPerPage, setRecordPerPage],
    recPP: [recPP, setRecPP],
    startSlice: [startSlice, setStartSlice],
    endSlice: [endSlice, setEndSlice],
    warehouses,
    deleteProduct,
    updateSettings,
    getProductsListSettings,
    getWarehouses,
    reloadProductsList,
    deleteSelectedProducts,
    products,
    addItemToDeleteList,
    removeItemsToDeleteList,
    clearItemToDeleteList,
    itemsToDelete,
    setAllCheck,
    recordFound,
    getTotalRecords,
    totalPage,
    filterProducts
  })
}

function ProductsListController() {
  const productsListController = useProductsListController()
  return (
    <React.Fragment>
      <productsListControllerContext.Provider value={ productsListController }>
        <ProductsListHeader />
        <ProductsListInner />
        <ProductsListFooter />
      </productsListControllerContext.Provider>
    </React.Fragment>
  )
}

export default React.memo(ProductsListController)